import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import  LogoContainer from '../../../../../shared/components/LogoContainer'
import { selectAccounts, fetchAccounts } from "../../../../../../app/store/slices/accountSlice";
import { createBudget } from "../../../../../../app/store/slices/budgetSlice";
import { toast } from 'react-toastify';
import  AddAnotherList from '../../../../../shared/components/AddAnotherList';
import {formatAmount} from '../../../../../../utils/formatAmount';

const BudgetManualSetup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const accounts = useSelector(selectAccounts);
  const [selectedCanvasId] = useState(location.state?.canvasId || JSON.parse(localStorage.getItem('onboardingCanvas'))?._id || '');
  const [selectedAccountId] = useState(location.state?.accountId || '');
  const initialExpense = { item: "", category: "", cost: "", account: selectedAccountId };
  const [expenses, setExpenses] = useState([]);
  const [currentExpense, setCurrentExpense] = useState(initialExpense);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (selectedCanvasId) {
      dispatch(fetchAccounts({ canvasId: selectedCanvasId, limit:1000 }));
    }
  }, [dispatch, selectedCanvasId]);
  const getAccountById = (id) => accounts.find(acc => acc._id === id);

  // Handlers
  const handleInputChange = (field, value) => {
    setCurrentExpense(exp => ({...exp, [field]: value }));
    setError("");
  };

  const handleAddExpense = () => {
    setError("");
    if (
      !currentExpense.item ||
      !currentExpense.category ||
      !currentExpense.cost ||
      !currentExpense.account
    ) {
      setError("Please fill all fields.");
      return;
    }
    const account = getAccountById(currentExpense.account);
    if (!account) {
      setError("Please select a valid account.");
      return;
    }
    setExpenses([...expenses, currentExpense]);
    setCurrentExpense(initialExpense);
  };

  const handleContinue = async () => {
    setError("");
    setIsProcessing(true);
    try {
      let allExpenses = [...expenses];
      if (
        currentExpense.item &&
        currentExpense.category &&
        currentExpense.cost &&
        currentExpense.account
      ) {
        const account = getAccountById(currentExpense.account);
        if (!account) {
          setError("Please select a valid account.");
          setIsProcessing(false);
          return;
        }
        allExpenses = [...expenses, currentExpense];
      }      
      for (const exp of allExpenses) {
        await dispatch(createBudget({
          canvasId: selectedCanvasId,
          budgetData: {
            itemName: exp.item,
            category: exp.category,
            frequency: "Monthly",
            allocationPercent: "",
            plannedCost: Number(exp.cost),
            actualCost: Number(exp.cost),
            account: exp.account,
            tags:'',
          }
        })).unwrap();
      }
      navigate('/saving', { state: { canvasId: selectedCanvasId, accountId: selectedAccountId } });
    } catch (err) {
      toast.error(err)
      console.error('Failed to create budget:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <LogoContainer/>
      <div className="card-setup">
        <h2 className="txt-page-heading">Enter your Fixed Expenses</h2>
        <p className="p-tag-below-heading text-center mb-8">
          Enter your monthly fixed recurring expenses
        </p>
        <div className="w-full flex flex-col items-center">
          <div className="space-y-4 w-full max-w-lg mx-auto">
            {/* Current Expense Input */}
            <div className="space-y-4">
              {/* Item Name */}
              <div className="flex items-center justify-center gap-3 w-full">
                <label className="sm:w-44 w-32 text-left font-medium text-gray-700 text-base">
                  Item
                </label>
                <input
                  type="text"
                  name="item"
                  value={currentExpense.item}
                  onChange={e => handleInputChange("item", e.target.value)}
                  placeholder="Enter Item Name"
                  className="input-field flex-1 w-full max-w-[400px]"
                />
              </div>
              {/* Category */}
              <div className="flex items-center justify-center gap-3 w-full">
                <label className="sm:w-44 w-32 text-left font-medium text-gray-700 text-base">
                  Category
                </label>
                <input
                  type="text"
                  name="category"
                  value={currentExpense.category}
                  onChange={e => handleInputChange("category", e.target.value)}
                  placeholder="eg. Insurance"
                  className="input-field flex-1 w-full max-w-[400px]"
                />
              </div>
              {/* Monthly Cost */}
              <div className="flex items-center justify-center gap-3 w-full">
                <label className="sm:w-44 w-32 text-left font-medium text-gray-700 text-base">
                  Monthly Cost
                </label>
                <input
                  type="number"
                  name="cost"
                  value={currentExpense.cost}
                  onChange={e => handleInputChange("cost", e.target.value)}
                  placeholder="Enter Amount"
                  className="input-field flex-1 w-full max-w-[400px]"
                  min="0"
                  step="0.01"
                />
              </div>
              {/* Amount Deduction Account */}
              <div className="flex items-center justify-center gap-3 w-full">
                <label className="sm:w-44 w-32 text-left font-medium text-gray-700 text-base">
                  Payment Account
                </label>
                <select
                  name="account"
                  value={currentExpense.account || ''}
                  onChange={e => handleInputChange("account", e.target.value)}
                  className="input-field flex-1 w-full max-w-[400px] text-gray-500"
                  required
                >
                  <option value="">Select Account</option>
                  {accounts.map(acc => (
                    <option key={acc._id} value={acc._id}>
                      {acc.name} ({acc.currency}) - Balance: {acc.balance}
                    </option>
                  ))}
                </select>
              </div>
              {/* Error message */}
              {error && (
                <div className="text-red-600 text-sm font-semibold mt-2">{error}</div>
              )}
              {/* Add Another Expense Button */}
              <div className="flex flex-row justify-end gap-6 w-full max-w-lg">
                <button
                  type="button"
                  className="btn-continue"
                  onClick={handleAddExpense}
                >
                  Add Another Expense
                </button>
              </div>
            </div>
          </div>
        </div>
        <AddAnotherList
            title="Your Expenses"
            items={expenses}
            className="w-full max-w-lg mx-auto"
            renderItem={(exp) => (
              <>
                <div>
                  <div className="font-medium">{exp.item}</div>
                  <div className="text-sm text-gray-500">{exp.category}</div>
                </div>
                <div className="text-sm text-gray-500">
                  {formatAmount(exp.cost)}
                </div>
              </>
            )}
          />
          
        <div className="flex flex-row justify-between items-center w-full mt-12">
          <button
            className="btn-back"
            onClick={() => navigate(-1)}
            type="button"
            disabled={isProcessing}
          >
            Back
          </button>
          <button
            className="btn-continue"
            onClick={handleContinue}
            type="button"
          >
            {isProcessing ? 'Processing...' : 'Save & Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BudgetManualSetup;